import _ from 'lodash';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    PANEL_PRIORITIES,
    STATUS_LIST_PANEL_PREVIEW_IMAGE,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import { filterId as networkDeviceFilterId } from '../../filters/network-device-filter/NetworkDeviceFilter';
import { filterId as accessPointFilterId } from '../../filters/access-point-filter/AccessPointFilter';
import { filterId as wlcFilterId } from '../../filters/wireless-controller-filter/WirelessControllerFilter';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import { metricDefFormatter } from '../../../utilities';
import { listTooltip, percentConfig, percentSparkLinkFn } from './funcs/IpmList';
import {
    businessServiceFilterId,
    networkDeviceTypeFilterId,
    siteFilterId,
    statusFilterId,
    timeRangeFilterId,
} from '../../filters';

const title = t`Network Devices`;

const dataSource = {
    url: '/query/table',
    params: {
        type: 'device',
        entityFields: ['ipAddress', 'name', 'deviceType', 'sysName', 'description'],
        host: true,
        metrics: ['status', 'avgCpu', 'avgMem'],
        trends: ['avgCpu', 'avgMem', 'status'],
        renderAsEntity: true,
    },
};

// Note that a number of these are currently marked as "unmonitored" by IPM (so they will not have any
// performance data). However, rules change over time, so include all of these types here just in case.
const deviceTypes = {
    switch: 'Switch',
    router: 'Router',
    firewall: 'Firewall',
    generic: 'Generic',
    wirelesscontroller: 'Wireless Controller',
    accesspoint: 'Access Point',
    idrac: 'iDrac',
    iot: 'IOT',
    'packet-processor': 'Packet Processor',
    'ups-psu': 'UPS/PSU',
    kvm: 'KVM',
    nas: 'NAS',
    'raid-management': 'RAID Management',
};

const linker = (row) => {
    const selector = _.get(row, 'device.deviceType');
    let target;
    let filterId;
    switch (selector) {
        case 'accesspoint':
            target = '/access-point-detail';
            filterId = accessPointFilterId;
            break;
        case 'wirelesscontroller':
            target = '/wireless-controller-detail';
            filterId = wlcFilterId;
            break;
        default:
            target = '/device-detail';
            filterId = networkDeviceFilterId;
    }
    return {
        target,
        mappedFilterParams: {
            [filterId]: 'device',
        },
    };
};

const CPU = t`CPU`;
const MEMORY = t`Memory`;

const columns = [
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`Device`,
        sortable: true,
        mapping: 'device.name',
        getTooltip: listTooltip('device.name', 'device.description'),
        width: 170,
        link: linker,
    },
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`IP Address`,
        sortable: true,
        mapping: 'device.ipAddress',
        getTooltip: listTooltip('device.ipAddress'),
        width: 120,
        link: linker,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Type`,
        sortable: true,
        sortProperty: 'device.deviceType',
        mapper: (row) => deviceTypes[row?.device?.deviceType] || t`Unknown`,
        width: 120,
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        title: '',
        assocId: CPU, // associated with CPU column
        csvTitle: CPU,
        mapping: 'avgCpu',
        width: 60,
        formatter: metricDefFormatter(percentConfig),
    },
    {
        type: COLUMN_TYPES.SPARKLINE,
        title: CPU,
        sortable: true,
        sortProperty: 'avgCpu',
        width: 120,
        mapper: (rowData) => percentSparkLinkFn('avgCpu', 'CPU Usage')(rowData),
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        title: '',
        assocId: MEMORY, // associated with Memory column
        csvTitle: MEMORY,
        mapping: 'avgMem',
        width: 60,
        formatter: metricDefFormatter(percentConfig),
    },
    {
        type: COLUMN_TYPES.SPARKLINE,
        title: MEMORY,
        sortable: true,
        sortProperty: 'avgMem',
        width: 120,
        mapper: (rowData) => percentSparkLinkFn('avgMem', 'Mem Usage')(rowData),
    },
    {
        type: COLUMN_TYPES.STATUSTREND,
        title: t`Status Trend`,
        sortable: true,
        sortProperty: 'status',
        mapping: 'trends.status.data',
        minWidth: 240,
    },
];

const sorters = [{ property: 'status', direction: 'desc' }];

function DeviceListPanel(props) {
    const loader = useQueryLoader(dataSource);
    return (
        <ReportingDataTable
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            deletedMapping="device.deleted"
        />
    );
}

DeviceListPanel.propTypes = panelPropTypes;

DeviceListPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'DeviceList',
    catalogEntry: {
        category: PANEL_CATEGORIES.DEVICE,
        priority: PANEL_PRIORITIES.LIST,
        title,
        description: t`List of network devices, showing status trends`,
        previewImage: STATUS_LIST_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId],
        optional: [businessServiceFilterId, networkDeviceTypeFilterId, siteFilterId, statusFilterId],
    },
})(DeviceListPanel);
